import { Box, Grid, Typography, useTheme } from "@mui/material";
import { TRADE_SIDE, WALLET_TYPE } from "../../constants";
import {
  cancelOrder,
  getMarginOpenOrderList,
  getSpotOpenOrderList,
  getTradeAmountMax,
} from "store/slices/trade/tradeSlice";
import { useParams, useSearchParams } from "react-router-dom";

import EmptyState from "components/EmptyState";
import { EmptyStateIcon } from "assets/Icon";
import HistoryBox from "./HistoryBox";
import NumberFormats from "components/NumberFormats";
import OrderCancelButton from "pages/History/TradeHistory/Components/OrderCancelButton";
import Pagination from "components/Pagination";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSelector } from "react-redux";
import { useState } from "react";

const headers = [
  {
    key: "side",
    name: "Side",
    align: "left",
  },
  {
    key: "walletType",
    name: "Wallet Type",
    align: "center",
  },
  {
    key: "type",
    name: "Type",
    align: "center",
  },
  {
    key: "market",
    name: "Market",
    align: "center",
  },
  {
    key: "filled",
    name: "Amount",
    align: "center",
  },
  {
    key: "price",
    name: "Price",
    align: "center",
  },
  {
    key: "fee",
    name: "Fee",
    align: "center",
  },
  {
    key: "total",
    name: "Total",
    align: "center",
  },
  {
    key: "date",
    name: "Date",
    align: "center",
  },
  {
    key: "action",
    name: "Action",
    align: "center",
  },
];

function OpenOrderHistory({ getInitialTradeHistory, loading, onCancel }) {
  const isMobileView = useIsMobileView();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { source, destination } = useParams();

  const [searchParams] = useSearchParams();

  const tradeHistory = useSelector((state) => state.trade.tradeHistory);
  const walletTypeId = +searchParams.get("walletTypeId") || 1;
  const sideId = +searchParams.get("sideId") || 1;

  const [loadingOrderId, setLoadingOrderId] = useState(null)

  const handleCancelOrder = async (order) => {
    setLoadingOrderId(order?.id)
    try {
      await dispatch(cancelOrder({ orderId: order?.id }), "سفارش شما با موفقیت کنسل شد.");
      setLoadingOrderId(null)

      if (onCancel) {
        onCancel();
      }

      dispatch(
        getTradeAmountMax({
          coinId: source,
          coinMasterId: destination,
          sideId,
          walletTypeId: walletTypeId,
        })
      );

      await getInitialTradeHistory();
    } catch (e) {
      this.notify(e?.response?.data?.message);
    }
  };

  const formattedItems = tradeHistory?.data?.map((item) => ({
    ...item,
    color:
      item?.sideId === TRADE_SIDE.Buy.id
        ? `${theme.palette.success.main}10`
        : `${theme.palette.error.main}10`,
    date: `${item?.date} ${item?.time}`,
    market: (
      <>
        <Typography variant="h6" component="span" color="text.main">
          {item?.coinSourceName}
        </Typography>
        /
        <Typography
          variant="subtitle1"
          component="small"
          color="text.secondary"
        >
          {item?.coinDestinationName}
        </Typography>
      </>
    ),
    side: (
      <Typography
        color={
          item?.sideId === TRADE_SIDE.Buy.id
            ? theme.palette.success.main
            : theme.palette.error.main
        }
      >
        {item?.side}
      </Typography>
    ),
    filled: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.filled} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinSourceName}
        </Typography>
      </Typography>
    ),
    fee: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.fee} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinFeeName}
        </Typography>
      </Typography>
    ),
    price: (
      <Typography color="text.main" fontWeight={500} variant="h6">
        <NumberFormats value={item?.price} />{" "}
        <Typography
          component="span"
          color="text.secondary"
          fontWeight={400}
          variant="subtitle1"
        >
          {item?.coinDestinationName}
        </Typography>
      </Typography>
    ),
    total: (
      <>
        <Typography color="text.main" fontWeight={500} variant="h6">
          <NumberFormats value={item?.total} />{" "}
          <Typography
            component="span"
            color="text.secondary"
            fontWeight={400}
            variant="subtitle1"
          >
            {item?.coinDestinationName}
          </Typography>
        </Typography>
      </>
    ),
    action: <OrderCancelButton onClick={handleCancelOrder} item={item} loading={loadingOrderId === item?.id} />,
  }));

  const handleChangePaginate = ({ page, perPage }) => {
    if (walletTypeId === WALLET_TYPE.margin) {
      dispatch(
        getMarginOpenOrderList({
          coinSourceId: -1,
          coinDestinationId: -1,
          sideId: 0,
          typeId: 0,
          pageNumber: page,
          pageSize: perPage,
        })
      );
    } else {
      dispatch(
        getSpotOpenOrderList({
          coinSourceId: -1,
          coinDestinationId: -1,
          sideId: 0,
          typeId: 0,
          pageNumber: page,
          pageSize: perPage,
        })
      );
    }
  };

  return (
    <>
      {isMobileView ? (
        <Grid container spacing={1}>
          {tradeHistory?.data?.length ? (
            tradeHistory?.data?.map((item) => (
              <Grid item xs={12}>
                <HistoryBox
                  item={item}
                  hasCancel
                  onCancel={handleCancelOrder}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <EmptyState
                text="No History"
                icon={<EmptyStateIcon color={theme.palette.text.secondary} />}
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <>
          {!loading ? (
            <Table headers={headers} items={formattedItems} />
          ) : (
            <TableSkeleton />
          )}
        </>
      )}

      <Box mt={2}>
        <Pagination
          perPage={tradeHistory?.pageSize}
          count={tradeHistory?.totalPages}
          onChange={handleChangePaginate}
        />
      </Box>
    </>
  );
}

export default OpenOrderHistory;
